import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, IconButton, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import WatchOutlinedIcon from "@mui/icons-material/WatchOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { off, onValue, ref, set } from "firebase/database";

import { AuthContext } from "../../context/AuthContext";
import { DarkModeContext } from "../../context/darkModeContext";
import { database } from "../../firebase";
import { SIDEBAR_TOGGLE_EVENT } from "../sidebar/SideBarForRole7";
import WatchGuardianLogo2 from "../../Images/HealthLogo.png";
import "./navbar.scss";

const roleLabel = (role) => {
  const value = Number(role);
  if (value === 1) return "Watch admin";
  if (value === 2) return "Company admin";
  return "User";
};

const NavbarForRole7 = ({ onAddMetric }) => {
  const { currentUser } = useContext(AuthContext);
  const { darkMode, dispatch: themeDispatch } = useContext(DarkModeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { watchId } = useParams();

  const [profilePicture, setProfilePicture] = useState(null);
  const [isCancellingFetch, setIsCancellingFetch] = useState(false);
  const [onDemandSensorValue, setOnDemandSensorValue] = useState(-1);

  const isOnSelectWatch =
    location.pathname === "/" || location.pathname.includes("/selectWatch");
  const isOnDashboard = location.pathname.includes("/dashboard");

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const profilePicRef = ref(database, `users/${currentUser.uid}/profilePicture`);
    const handler = onValue(profilePicRef, (snapshot) =>
      setProfilePicture(snapshot.val())
    );

    return () => off(profilePicRef, "value", handler);
  }, [currentUser]);

  useEffect(() => {
    if (!watchId) return undefined;

    const onDemandRef = ref(database, `Watches/${watchId}/onDemandSensor`);
    const handler = onValue(onDemandRef, (snapshot) =>
      setOnDemandSensorValue(snapshot.exists() ? snapshot.val() : -1)
    );

    return () => off(onDemandRef, "value", handler);
  }, [watchId]);

  const isFetching = onDemandSensorValue !== -1;

  const handleCancelOnDemandFetch = async () => {
    if (!watchId || isCancellingFetch) return;

    setIsCancellingFetch(true);
    try {
      await Promise.allSettled([
        set(ref(database, `Watches/${watchId}/onDemandSensor`), -1),
        set(ref(database, `Health/${watchId}/onDemandSensor`), -1),
      ]);
    } catch (error) {
      console.error("Failed to cancel on-demand fetch", error);
      alert("Unable to stop the current data request. Please try again.");
    } finally {
      setIsCancellingFetch(false);
    }
  };

  const initial =
    currentUser?.displayName?.[0] || currentUser?.email?.[0] || "U";

  return (
    <header className="navbar">
      <Tooltip title="Menu">
        <IconButton
          className="navbar__menu"
          aria-label="Toggle navigation"
          onClick={() => window.dispatchEvent(new CustomEvent(SIDEBAR_TOGGLE_EVENT))}
        >
          <MenuIcon />
        </IconButton>
      </Tooltip>

      <div className="navbar__brand">
        <img src={WatchGuardianLogo2} alt="Watch Guardian Health" className="navbar__logo" />
        {watchId && (
          <div className="navbar__context">
            <span className="navbar__context-label">Watch</span>
            <span className="navbar__context-value wg-mono">{watchId}</span>
          </div>
        )}
      </div>

      <div className="navbar__actions">
        {!isOnSelectWatch && watchId && (
          <span
            className={`wg-chip ${isFetching ? "wg-chip--accent" : ""}`}
            title={
              isFetching
                ? "A data request is currently running on the watch"
                : "No data request is running"
            }
          >
            <span
              className={`wg-chip__dot ${isFetching ? "wg-chip__dot--pulse" : ""}`}
              style={{ background: isFetching ? undefined : "var(--wg-ink-muted)" }}
            />
            {isFetching ? "Fetching data" : "Idle"}
          </span>
        )}

        {!isOnSelectWatch && (
          <Button
            className="navbar__cta"
            variant="outlined"
            size="small"
            startIcon={<WatchOutlinedIcon />}
            onClick={() =>
              navigate(
                `/${currentUser.companyId}/${currentUser.locationId}/selectWatch`
              )
            }
          >
            <span className="navbar__label">Select a watch</span>
          </Button>
        )}

        {isOnDashboard && onAddMetric && (
          <Button
            className="navbar__cta"
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddMetric}
          >
            <span className="navbar__label">Add metric</span>
          </Button>
        )}

        {!isOnSelectWatch && watchId && (
          <Tooltip
            title={
              isCancellingFetch
                ? "Cancelling the current data request…"
                : isFetching
                ? "Stop the current data request"
                : "No active on-demand request to stop"
            }
          >
            <span>
              <Button
                className="navbar__cta"
                variant="outlined"
                color="error"
                size="small"
                startIcon={<StopCircleOutlinedIcon />}
                disabled={isCancellingFetch || !isFetching}
                onClick={handleCancelOnDemandFetch}
              >
                <span className="navbar__label">
                  {isCancellingFetch ? "Stopping…" : "Stop fetch"}
                </span>
              </Button>
            </span>
          </Tooltip>
        )}

        <span className="navbar__divider" />

        <Tooltip title={darkMode ? "Switch to light theme" : "Switch to dark theme"}>
          <IconButton
            aria-label="Toggle colour theme"
            onClick={() => themeDispatch({ type: "TOGGLE" })}
          >
            {darkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>

        <div className="navbar__user">
          {profilePicture ? (
            <img src={profilePicture} alt="" className="navbar__avatar" />
          ) : (
            <span className="navbar__avatar" aria-hidden="true">
              {initial}
            </span>
          )}
          <span className="navbar__user-meta">
            <span className="navbar__user-name">
              {currentUser?.displayName || currentUser?.email || "Signed in"}
            </span>
            <span className="navbar__user-role">{roleLabel(currentUser?.role)}</span>
          </span>
        </div>
      </div>
    </header>
  );
};

export default NavbarForRole7;
